<template>
  <DxPopup
    :visible="visible"
    @hiding="onHiding"
    @shown="popupShown"
    :width="'80%'"
    :height="'auto'"
    :show-title="true"
    :title="popupTitle"
    :drag-enabled="false"
    :close-on-outside-click="false"
    :show-close-button="true"
    :wrapper-attr="{ class: 'css-v2 gc-popup-customer-transaction-detail' }"
  >
    <template #content>
      <div>
        <div class="txn-dtl-header">
          <div class="txn-dtl-field">
            <span class="txn-dtl-label">Date</span>
            <span>{{ formatDate(transaction.DateIn) }}</span>
          </div>
          <div class="txn-dtl-field">
            <span class="txn-dtl-label">Due Date</span>
            <span>{{ formatDate(transaction.InvoiceHeaderDateDue) }}</span>
          </div>
          <div class="txn-dtl-field">
            <span class="txn-dtl-label">Sales Order</span>
            <span>{{ transaction.SalesOrder }}</span>
          </div>
          <div class="txn-dtl-field">
            <span class="txn-dtl-label">Status</span>
            <GcStatusChip :status="transaction.CustomerTransactionStatus" />
          </div>
          <div class="txn-dtl-field">
            <span class="txn-dtl-label">{{ `Total (${transaction.Currency})` }}</span>
            <span :class="{ 'txn-dtl-negative': transaction.Total < 0 }">{{ formatCurrency(transaction.Total) }}</span>
          </div>
          <div class="txn-dtl-field">
            <span class="txn-dtl-label">{{ `Total (${transaction.FXCurrency})` }}</span>
            <span :class="{ 'txn-dtl-negative': transaction.FXTotal < 0 }">{{ formatCurrency(transaction.FXTotal) }}</span>
          </div>
          <div class="txn-dtl-field">
            <span class="txn-dtl-label">{{ `Balance (${transaction.Currency})` }}</span>
            <span :class="{ 'txn-dtl-negative': transaction.Balance < 0 }">{{ formatCurrency(transaction.Balance) }}</span>
          </div>
          <div class="txn-dtl-field">
            <span class="txn-dtl-label">{{ `Balance (${transaction.FXCurrency})` }}</span>
            <span :class="{ 'txn-dtl-negative': transaction.FXBalance < 0 }">{{ formatCurrency(transaction.FXBalance) }}</span>
          </div>
        </div>

        <div v-if="!loaded" class="popup-loading">
          <DxLoadIndicator :height="24" :width="24" />
        </div>

        <DxDataGrid
          v-else
          :data-source="lineItems"
          :show-borders="true"
          :show-column-lines="true"
          :show-row-lines="true"
          :column-auto-width="true"
          :columns="columns"
          :paging="{ enabled: false }"
          :pager="{ visible: false }"
          :column-chooser="{ enabled: false }"
          :search-panel="{ visible: false }"
          @cell-prepared="onCellPrepared"
        >
          <template #product-account-cell-template="{ data }">
            <div class="txn-dtl-product-cell">
              <div class="txn-dtl-product-primary">{{ data.data.ItemDescription }}</div>
              <div class="txn-dtl-product-secondary">{{ data.data.ItemCode }}</div>
            </div>
          </template>
        </DxDataGrid>
      </div>
    </template>
  </DxPopup>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import { DxDataGrid } from "devextreme-vue/data-grid";
import { DxLoadIndicator } from "devextreme-vue/load-indicator";
import GcStatusChip from "../../components/gc-status-chip.vue";
import { InvoiceService } from "../../_services/api/sales/invoice.service";
import { CustomerReceiptService } from "../../_services/api/customers/customer-receipt-service";
import { getDecimalFormat } from "../../_store/helpers";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";

const INVOICE_DETAIL_SELECT = [
  "ItemCode",
  "ItemDescription",
  "TaxCode",
  "SalesUM",
  "SalesUMOrderQty",
  "SalesUMQty",
  "SalesUMCreditedQty",
  "SalesUMFXPrice",
  "DiscPcnt",
  "SalesUMNetFXPrice",
  "TaxableFXLineTotal",
  "TaxFXLineTotal",
  "FXLineTotal",
  "Comment",
  "LineNo",
];

const INVOICE_TYPES = ["Invoice", "Credit"];

export default {
  name: "GcPopupCustomerTransactionDetail",
  components: { DxPopup, DxDataGrid, DxLoadIndicator, GcStatusChip },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    transaction: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loaded: false,
      lineItems: [],
    };
  },
  computed: {
    decimalFormat() {
      return getDecimalFormat("Decimal-16-2").ReadOnlyFormat;
    },
    percentFormat() {
      return getDecimalFormat("Decimal-16-2P").ReadOnlyFormat;
    },
    quantityFormat() {
      return getDecimalFormat("Decimal-16-4").ReadOnlyFormat;
    },
    priceFormat() {
      return getDecimalFormat("Decimal-16-4").ReadOnlyFormat;
    },
    exchangeRateFormat() {
      return getDecimalFormat("Decimal-6-4").ReadOnlyFormat;
    },
    isInvoiceType() {
      return INVOICE_TYPES.includes(this.transaction.CustomerTransactionType);
    },
    isCreditICFlagI() {
      return this.transaction.CustomerTransactionType === "Credit" && this.transaction.ICFlag === "I";
    },
    isCreditICFlagC() {
      return this.transaction.CustomerTransactionType === "Credit" && this.transaction.ICFlag === "C";
    },
    popupTitle() {
      return `${this.transaction.CustomerTransactionType} ${this.transaction.CustomerTransactionNumber}`;
    },
    productAccountColumn() {
      return {
        caption: this.isCreditICFlagI ? "Account" : "Product / Account",
        dataField: "ItemCode",
        cellTemplate: "product-account-cell-template",
        width: ColumnWidth.ProductOrAccount.width,
        minWidth: ColumnWidth.ProductOrAccount.minWidth,
      };
    },
    columns() {
      // Invoice — mirrors src/views/sales/invoice.vue's review-mode line-item grid
      if (this.transaction.CustomerTransactionType === "Invoice") {
        return [
          this.productAccountColumn,
          {
            caption: "Tax",
            dataField: "TaxCode",
            width: ColumnWidth.TaxCode.width,
            minWidth: ColumnWidth.TaxCode.minWidth,
          },
          {
            caption: "UM",
            dataField: "SalesUM",
            width: ColumnWidth.UnitOfMeasure.width,
            minWidth: ColumnWidth.UnitOfMeasure.minWidth,
          },
          {
            caption: "Order Qty",
            dataField: "SalesUMOrderQty",
            alignment: "right",
            format: this.quantityFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.Quantity4DecimalPoints.width,
            minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          },
          {
            caption: "Invoiced Qty",
            dataField: "SalesUMQty",
            alignment: "right",
            format: this.quantityFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.Quantity4DecimalPoints.width,
            minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          },
          {
            caption: `Price (${this.transaction.Currency})`,
            dataField: "SalesUMFXPrice",
            alignment: "right",
            format: this.priceFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.Price4DecimalPoints.width,
            minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          },
          {
            caption: "Discount %",
            dataField: "DiscPcnt",
            alignment: "right",
            format: this.percentFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.Percentage.width,
            minWidth: ColumnWidth.Percentage.minWidth,
          },
          {
            caption: `Net (${this.transaction.Currency})`,
            dataField: "SalesUMNetFXPrice",
            alignment: "right",
            format: this.priceFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.Price4DecimalPoints.width,
            minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          },
          {
            caption: `Ex Tax (${this.transaction.Currency})`,
            dataField: "TaxableFXLineTotal",
            alignment: "right",
            format: this.decimalFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.TaxableFXLineTotal.width,
            minWidth: ColumnWidth.TaxableFXLineTotal.minWidth,
          },
          {
            caption: `Tax (${this.transaction.Currency})`,
            dataField: "TaxFXLineTotal",
            alignment: "right",
            format: this.decimalFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.TaxFXLineTotal.width,
            minWidth: ColumnWidth.TaxFXLineTotal.minWidth,
          },
          {
            caption: `Inc Tax (${this.transaction.Currency})`,
            dataField: "FXLineTotal",
            alignment: "right",
            format: this.decimalFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.TotalAmount.width,
            minWidth: ColumnWidth.TotalAmount.minWidth,
          },
          {
            caption: "Comment",
            dataField: "Comment",
            width: ColumnWidth.Comment.width,
            minWidth: ColumnWidth.Comment.minWidth,
          },
        ];
      }

      // Credit note (ICFlag=I) — mirrors src/views/customers/customer-credit.vue's review-mode grid
      if (this.isCreditICFlagI) {
        return [
          this.productAccountColumn,
          {
            caption: "Tax",
            dataField: "TaxCode",
            width: ColumnWidth.TaxCode.width,
            minWidth: ColumnWidth.TaxCode.minWidth,
          },
          {
            caption: "UM",
            dataField: "SalesUM",
            width: ColumnWidth.UnitOfMeasure.width,
            minWidth: ColumnWidth.UnitOfMeasure.minWidth,
          },
          {
            caption: "Credit Qty",
            dataField: "SalesUMQty",
            alignment: "right",
            format: this.quantityFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.Quantity4DecimalPoints.width,
            minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          },
          {
            caption: `Net (${this.transaction.Currency})`,
            dataField: "SalesUMNetFXPrice",
            alignment: "right",
            format: this.priceFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.Price4DecimalPoints.width,
            minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          },
          {
            caption: `Ex Tax (${this.transaction.Currency})`,
            dataField: "TaxableFXLineTotal",
            alignment: "right",
            format: this.decimalFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.TaxableFXLineTotal.width,
            minWidth: ColumnWidth.TaxableFXLineTotal.minWidth,
          },
          {
            caption: `Tax (${this.transaction.Currency})`,
            dataField: "TaxFXLineTotal",
            alignment: "right",
            format: this.decimalFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.TaxFXLineTotal.width,
            minWidth: ColumnWidth.TaxFXLineTotal.minWidth,
          },
          {
            caption: `Inc Tax (${this.transaction.Currency})`,
            dataField: "FXLineTotal",
            alignment: "right",
            format: this.decimalFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.TotalAmount.width,
            minWidth: ColumnWidth.TotalAmount.minWidth,
          },
          {
            caption: "Comment",
            dataField: "Comment",
            width: ColumnWidth.Comment.width,
            minWidth: ColumnWidth.Comment.minWidth,
          },
        ];
      }

      // Credit against invoice (ICFlag=C) — mirrors src/views/sales/invoice-credit.vue's review-mode grid
      if (this.isCreditICFlagC) {
        return [
          this.productAccountColumn,
          {
            caption: "Tax",
            dataField: "TaxCode",
            width: ColumnWidth.TaxCode.width,
            minWidth: ColumnWidth.TaxCode.minWidth,
          },
          {
            caption: "UM",
            dataField: "SalesUM",
            width: ColumnWidth.UnitOfMeasure.width,
            minWidth: ColumnWidth.UnitOfMeasure.minWidth,
          },
          {
            caption: "Order Qty",
            dataField: "SalesUMOrderQty",
            alignment: "right",
            format: this.quantityFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.Quantity4DecimalPoints.width,
            minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          },
          {
            caption: "Outstanding Qty",
            calculateCellValue: (rowData) => rowData.SalesUMQty - rowData.SalesUMCreditedQty,
            alignment: "right",
            format: this.quantityFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.Quantity4DecimalPoints.width,
            minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          },
          {
            caption: "Credit Qty",
            dataField: "SalesUMQty",
            alignment: "right",
            format: this.quantityFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.Quantity4DecimalPoints.width,
            minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          },
          {
            caption: `Price (${this.transaction.Currency})`,
            dataField: "SalesUMNetFXPrice",
            alignment: "right",
            format: this.priceFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.Price4DecimalPoints.width,
            minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          },
          {
            caption: `Ex Total (${this.transaction.Currency})`,
            dataField: "TaxableFXLineTotal",
            alignment: "right",
            format: this.decimalFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.TaxableFXLineTotal.width,
            minWidth: ColumnWidth.TaxableFXLineTotal.minWidth,
          },
          {
            caption: `Tax (${this.transaction.Currency})`,
            dataField: "TaxFXLineTotal",
            alignment: "right",
            format: this.decimalFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.TaxFXLineTotal.width,
            minWidth: ColumnWidth.TaxFXLineTotal.minWidth,
          },
          {
            caption: `Inc Total (${this.transaction.Currency})`,
            dataField: "FXLineTotal",
            alignment: "right",
            format: this.decimalFormat,
            customizeText: this.dashForZero,
            width: ColumnWidth.TotalAmount.width,
            minWidth: ColumnWidth.TotalAmount.minWidth,
          },
          {
            caption: "Comment",
            dataField: "Comment",
            width: ColumnWidth.Comment.width,
            minWidth: ColumnWidth.Comment.minWidth,
          },
        ];
      }

      // Receipt / Unallocated Receipt — mirrors src/views/customers/customer-receipt.vue's review-mode allocation grid
      return [
        {
          caption: "Type",
          dataField: "Type",
          width: ColumnWidth.InvoiceType.width,
          minWidth: ColumnWidth.InvoiceType.minWidth,
        },
        {
          caption: "Invoice / Ref",
          dataField: "LinkRefNo",
          width: ColumnWidth.InvoiceRef.width,
          minWidth: ColumnWidth.InvoiceRef.minWidth,
        },
        {
          caption: "Date",
          dataField: "DateIn",
          dataType: "date",
          format: "dd MMM yyyy",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
        },
        {
          caption: "Due Date",
          dataField: "CalculatedDueDate",
          dataType: "date",
          format: "dd MMM yyyy",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
        },
        {
          caption: "Sales Order",
          dataField: "SalesOrder",
          width: ColumnWidth.SalesOrderNumber.width,
          minWidth: ColumnWidth.SalesOrderNumber.minWidth,
        },
        {
          caption: "Cust PO",
          dataField: "CustomerOrder",
          width: ColumnWidth.CustPO.width,
          minWidth: ColumnWidth.CustPO.minWidth,
        },
        {
          caption: "Comment",
          dataField: "Comment",
          width: ColumnWidth.Comment.width,
          minWidth: ColumnWidth.Comment.minWidth,
        },
        {
          caption: "Exchange Rate",
          dataField: "FXRate",
          alignment: "right",
          format: this.exchangeRateFormat,
          customizeText: this.dashForZero,
          width: ColumnWidth.ExchangeRate.width,
          minWidth: ColumnWidth.ExchangeRate.minWidth,
        },
        {
          caption: `Discount (${this.transaction.Currency})`,
          dataField: "FXDiscount",
          alignment: "right",
          format: this.decimalFormat,
          customizeText: this.dashForZero,
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
        },
        {
          caption: `Discount (${this.transaction.FXCurrency})`,
          dataField: "DiscAmount",
          alignment: "right",
          format: this.decimalFormat,
          customizeText: this.dashForZero,
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
        },
        {
          caption: `Allocated (${this.transaction.Currency})`,
          dataField: "FXAllocationAmount",
          alignment: "right",
          format: this.decimalFormat,
          customizeText: this.dashForZero,
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
        },
        {
          caption: `Allocated (${this.transaction.FXCurrency})`,
          dataField: "Allocation",
          alignment: "right",
          format: this.decimalFormat,
          customizeText: this.dashForZero,
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
        },
        {
          caption: `Gain / Loss (${this.transaction.FXCurrency})`,
          dataField: "GainOrLoss",
          alignment: "right",
          format: this.decimalFormat,
          customizeText: this.dashForZero,
          width: ColumnWidth.Amount.width,
          minWidth: ColumnWidth.Amount.minWidth,
        },
      ];
    },
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        this.loaded = false;
        this.lineItems = [];
      }
    },
  },
  methods: {
    onHiding() {
      this.$emit("update:visible", false);
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "";
    },
    formatCurrency(value) {
      const amount = Number(value || 0);
      const formatted = Math.abs(amount).toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      return amount < 0 ? `(${formatted})` : formatted;
    },
    dashForZero(e) {
      return e.value ? e.valueText : "–";
    },
    onCellPrepared(e) {
      if (e.rowType !== "data" || e.column.alignment !== "right") {
        return;
      }

      if (e.value < 0) {
        e.cellElement.classList.add("negative-decimal");
      }
    },
    cashReceiptDetailPostProcess(data) {
      data.forEach((element) => {
        element.DateIn = element.Date;
        element.CalculatedDueDate =
          element.Type === "Prepayment" || element.Type === "Unallocated" ? null : element.DateChanged;
        element.FXAllocationAmount = element.FXAllocation;
        element.GainOrLoss = element.FXVariance;
      });
      return data;
    },
    async popupShown() {
      const code = this.transaction.CustomerTransactionNumber;

      if (this.isInvoiceType) {
        const response = await InvoiceService.getInvoiceDetails(code, {
          select: INVOICE_DETAIL_SELECT,
        });
        this.lineItems = response.data.value;
      } else {
        const response = await CustomerReceiptService.getCashReceiptDetail({
          filter: ["CashReceiptCode eq " + code],
        });
        this.lineItems = this.cashReceiptDetailPostProcess(response.data.value);
      }

      this.loaded = true;
    },
  },
};
</script>

<style lang="scss">
.gc-popup-customer-transaction-detail {
  .txn-dtl-header {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px 16px;
    padding-bottom: 14px;
    margin-bottom: 14px;
    border-bottom: 1px solid #edf0f3;
  }

  .txn-dtl-field {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 13px;
  }

  .txn-dtl-label {
    font-size: 11px;
    color: #667085;
  }

  .txn-dtl-negative {
    color: #d92d20;
  }

  .txn-dtl-field .gc-status-chip {
    align-self: flex-start;
  }

  .txn-dtl-product-cell {
    line-height: 1.3;
  }

  .txn-dtl-product-secondary {
    font-size: 11px;
    color: #667085;
  }

  .popup-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 0;
  }
}
</style>
